import { useState, useEffect, useRef } from 'react';
import { RESTORE_POLICY_FIXED_SECTIONS } from '../constants/restorePolicyFixedSections';
import {
  generateRestorePolicyAsync,
  getRestorePolicyJobStatus,
  parseRestorePolicy,
  RestorePolicyGenerationRequest,
  RestorePolicyJobStatus,
  RestorePolicyResponse,
} from '../services/restorePolicyApi';

interface UseRestorePolicyGenerationAsyncProps {
  uploadedFiles?: any[];
  apiResponse?: any;
  companyInfo?: {
    companyName: string;
    location: string;
    employeeCount?: string;
    businessDescription: string;
    criticalBusiness: string;
  };
  savedFormData?: Record<string, string>;
  skipAutoGeneration?: boolean;
  pollIntervalMs?: number; // ポーリング間隔(デフォルト: 2000ms)
  maxPollTimeMs?: number; // 最大ポーリング時間(デフォルト: 300000ms = 5分)
  searchQuery?: string; // ベクトル検索クエリ
  maxSearchResults?: number; // 最大検索結果数
  tenantId?: string; // テナントID
}

interface UseRestorePolicyGenerationAsyncResult {
  isLoading: boolean;
  formData: Record<string, string>;
  hasGenerated: boolean;
  error: string | null;
  jobStatus: RestorePolicyJobStatus | null;
  elapsedSeconds: number;
  regenerate: () => Promise<void>;
}

export const useRestorePolicyGenerationAsync = ({
  uploadedFiles = [],
  apiResponse,
  companyInfo,
  savedFormData,
  skipAutoGeneration,
  pollIntervalMs = 2000,
  maxPollTimeMs = 300000,
  searchQuery,
  maxSearchResults = 5,
  tenantId,
}: UseRestorePolicyGenerationAsyncProps): UseRestorePolicyGenerationAsyncResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>(() => savedFormData ?? {});
  const [hasGenerated, setHasGenerated] = useState(() => Boolean(savedFormData));
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<RestorePolicyJobStatus | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  // savedFormDataがある場合は既に生成済みとみなす
  const hasExecutedRef = useRef(Boolean(savedFormData));

  // アップロードされた情報から企業情報を抽出
  const extractCompanyInfo = () => {
    const extractedInfo: Partial<RestorePolicyGenerationRequest> = {
      company_name: '',
      business_type: '',
      location: '',
    };

    // merged_extracted_info を優先的に使用(簡易版と同じ動作)
    const mergedInfo = apiResponse?.merged_extracted_info || {};
    const rawExtractedInfo = apiResponse?.extracted_info || {};

    // companyInfo(CompanyInfoページからの変更)を最優先で使用
    if (companyInfo) {
      extractedInfo.company_name = companyInfo.companyName || '';
      extractedInfo.location = companyInfo.location || '';
      extractedInfo.business_type = companyInfo.businessDescription || '';
      extractedInfo.main_business = companyInfo.criticalBusiness || '';

      if (companyInfo.employeeCount) {
        const parsed = parseInt(companyInfo.employeeCount, 10);
        extractedInfo.employee_count = isNaN(parsed) ? undefined : parsed;
      }

      // extracted_infoを構築(companyInfoベース + rawExtractedInfoの詳細情報)
      extractedInfo.extracted_info = {
        ...rawExtractedInfo,
        company_name: extractedInfo.company_name,
        address: extractedInfo.location,
        business_content: extractedInfo.business_type,
        main_business: extractedInfo.main_business,
        employees: extractedInfo.employee_count,
      };
    } else {
      // companyInfoがない場合はmerged_extracted_infoとextracted_infoから取得
      extractedInfo.company_name = mergedInfo.company_name || mergedInfo.company || '';
      extractedInfo.business_type = mergedInfo.business_type || mergedInfo.business_content || mergedInfo.industry || '';
      // addressを優先(locationは古いデータの可能性があるため)
      extractedInfo.location = mergedInfo.address || mergedInfo.location || '';

      // extracted_info から補完(merged_extracted_info に値がない場合)
      if (!extractedInfo.company_name) {
        extractedInfo.company_name = rawExtractedInfo.company_name || rawExtractedInfo.company || '';
      }
      if (!extractedInfo.business_type) {
        extractedInfo.business_type = rawExtractedInfo.business_type || rawExtractedInfo.business_content || rawExtractedInfo.industry || '';
      }
      if (!extractedInfo.location) {
        // addressを優先(locationは古いデータの可能性があるため)
        extractedInfo.location = rawExtractedInfo.address || rawExtractedInfo.location || '';
      }

      // 詳細情報は extracted_info から取得
      if (rawExtractedInfo.main_business) {
        extractedInfo.main_business = rawExtractedInfo.main_business || rawExtractedInfo.business_description;
      }
      if (rawExtractedInfo.employee_count || rawExtractedInfo.employees) {
        extractedInfo.employee_count = rawExtractedInfo.employee_count || rawExtractedInfo.employees;
      }

      extractedInfo.extracted_info = rawExtractedInfo;
    }

    // 詳細情報(companyInfoにない項目)は extracted_info から取得
    if (rawExtractedInfo.annual_revenue || rawExtractedInfo.revenue) {
      extractedInfo.annual_revenue = rawExtractedInfo.annual_revenue || rawExtractedInfo.revenue;
    }

    // 取引先情報
    if (rawExtractedInfo.suppliers) {
      extractedInfo.suppliers = Array.isArray(rawExtractedInfo.suppliers) ? rawExtractedInfo.suppliers : [rawExtractedInfo.suppliers];
    }
    if (rawExtractedInfo.customers) {
      extractedInfo.customers = Array.isArray(rawExtractedInfo.customers) ? rawExtractedInfo.customers : [rawExtractedInfo.customers];
    }

    // ファイル名からも推測
    if (uploadedFiles.length > 0 && !extractedInfo.company_name) {
      const fileName = uploadedFiles[0].file.name;
      // ファイル名から企業名を推測（例：「株式会社○○_会社案内.pdf」）
      // アンダースコアまたはスペース+特定キーワードで区切る
      const companyMatch = fileName.match(/^(株式会社[^\s_]+|[^\s_]+株式会社)/);
      if (companyMatch) {
        extractedInfo.company_name = companyMatch[1];
      }
    }

    // デフォルト値を設定(必須項目が空の場合)
    if (!extractedInfo.company_name) {
      extractedInfo.company_name = '株式会社サンプル';
    }
    if (!extractedInfo.business_type) {
      extractedInfo.business_type = '製造業';
    }
    if (!extractedInfo.location) {
      extractedInfo.location = '東京都';
    }

    // extracted_info フィールドを構築（APIリクエストで使用）
    if (!extractedInfo.extracted_info) {
      extractedInfo.extracted_info = {
        company_name: extractedInfo.company_name,
        address: extractedInfo.location,
        business_content: extractedInfo.business_type,
        main_business: extractedInfo.main_business,
        employees: extractedInfo.employee_count,
        suppliers: extractedInfo.suppliers,
        customers: extractedInfo.customers,
      };
    }

    return extractedInfo;
  };

  // 検索クエリを生成 (既存のuseRestorePolicyGenerationと同じロジック)
  const generateSearchQuery = (companyInfo: any) => {
    const businessType = companyInfo.business_type || companyInfo.industry || '製造業';
    const location = companyInfo.location || companyInfo.address || '東京都';
    return `${businessType} 復旧方針 BCP ${location}`;
  };

  // ポーリングクリーンアップ
  const cleanupPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
  };

  // ジョブステータスをポーリング
  const pollJobStatus = async (jobId: string) => {
    startTimeRef.current = Date.now();
    setElapsedSeconds(0);

    // 経過時間更新用のインターバル
    elapsedIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    // ポーリング開始
    pollIntervalRef.current = setInterval(async () => {
      try {
        const jobResponse = await getRestorePolicyJobStatus(jobId, tenantId);
        setJobStatus(jobResponse.status);

        // 完了または失敗の場合、ポーリング停止
        if (jobResponse.status === 'completed' || jobResponse.status === 'failed') {
          cleanupPolling();
          setIsLoading(false);

          if (jobResponse.status === 'completed' && jobResponse.result) {
            // 結果を処理
            const result = jobResponse.result;

            // ドキュメントをparseRestorePolicy関数で各セクションに展開
            const parsedSections = parseRestorePolicy(result.restore_policy_document);
            parsedSections.financial_plan =
              RESTORE_POLICY_FIXED_SECTIONS.FINANCIAL_PLAN_2_5;

            // 修正: 条件を削除して常に実行
            // 理由: LLM生成のMarkdown記法を確実に排除し、簡易版BCPと一致させる
            //
            // 注意: バックエンド(restore_policy_worker.py:187)はcompany_infoのみ返す
            //       extracted_infoは含まれない
            //
            // company_infoの内容(restore_policy_worker.py:66-78):
            // - 初期値: customers キー(request.customers)
            // - マージ後: clients キー(extracted_info.clients)も追加される
            // - merge_company_infoは空値で上書きしないため、両方存在する可能性あり
            if (result.company_info) {
              const info = result.company_info;

              // 基本版BCP企業情報フォーマット: 企業名、所在地、従業員数、事業内容の4項目のみ
              const companyName = info.company_name || info.name || '';
              const address = info.address || info.location || '';
              const businessContent = info.business_content || '';
              const rawEmployees = info.employees || info.employee_count || '';
              // Add unit if not present
              const employees = rawEmployees && !/名|人/.test(String(rawEmployees))
                ? `${rawEmployees}名`
                : rawEmployees;

              // Build company info with simplified format (4 fields only)
              // Matching simple BCP format: 企業名、所在地、従業員数、事業内容
              parsedSections.company_info =
                `企業名：${companyName}\n` +
                `所在地：${address}\n` +
                `従業員数：${employees}\n` +
                `事業内容：${businessContent}`;
            } else {
              // result.company_infoが存在しない場合(異常ケース)
              //
              // 最終判断: 何もしない(現状維持)
              //
              // 挙動の詳細:
              // 1. parsedSections.company_infoの状態:
              //    - parseRestorePolicy()がLLM生成ドキュメントから抽出した文字列が入っている
              //    - 例: "**企業名**: 株式会社○○\n**所在地**: ..." (Markdown記法含む)
              //
              // 2. レンダリング時の挙動:
              //    - setFormData(parsedSections)により、formDataにそのまま設定される
              //    - フォームに表示される際、Markdown記法が含まれたテキストが表示
              //    - ユーザーは編集可能なため、手動で整形可能
              //
              // 3. 仕様上の許容:
              //    - ✓ 情報が失われない(LLM生成内容が保持される)
              //    - ✓ ユーザーが手動編集で対応可能
              //    - △ Markdown記法が混入するが、読解には支障なし
              //    - ✓ バックエンドは常にcompany_infoを返すため、実際には発生しない
              //
              // 理由:
              // 1. バックエンドは常にcompany_infoを返すため、実際には発生しない
              // 2. 将来的に発生した場合、LLM生成コンテンツ > 空テンプレート > エラー
              // 3. エラーにすると、ユーザーが復旧方針を確認できなくなる
              //
              // 実装しない代替案:
              // × 空テンプレート挿入: 情報が完全に失われる
              //   parsedSections.company_info = '企業名：\n代表者：\n所在地：\n...';
              // × エラー送出: ユーザー体験が悪化
              //   throw new Error('company_info is missing');

              console.warn(
                'result.company_info is missing. Using LLM-generated content as-is.'
              );
            }

            setFormData(parsedSections);
            setHasGenerated(true);
            setError(null);
          } else if (jobResponse.status === 'failed') {
            setError(jobResponse.error || '復旧方針の生成に失敗しました');
          }
        }

        // 最大ポーリング時間を超えた場合
        if (Date.now() - startTimeRef.current > maxPollTimeMs) {
          cleanupPolling();
          setIsLoading(false);
          setError('タイムアウト: 復旧方針の生成に時間がかかっています');
        }
      } catch (err: any) {
        cleanupPolling();
        setIsLoading(false);
        setError(err.message || 'ジョブステータスの取得中にエラーが発生しました');
      }
    }, pollIntervalMs);
  };

  // 復旧方針を生成(非同期)
  const generate = async () => {
    if (hasExecutedRef.current || isLoading) {
      return;
    }

    hasExecutedRef.current = true;
    setIsLoading(true);
    setError(null);
    setJobStatus(null);
    setElapsedSeconds(0);

    try {
      const extractedCompanyInfo = extractCompanyInfo();

      // デバッグログを追加
      console.log('=== extractCompanyInfo Debug ===');
      console.log('props.companyInfo:', companyInfo);
      console.log('apiResponse:', apiResponse);
      console.log('extracted_info:', apiResponse?.extracted_info);
      console.log('extractedCompanyInfo:', extractedCompanyInfo);
      console.log('extractedCompanyInfo.location:', extractedCompanyInfo.location);
      console.log('extractedCompanyInfo.extracted_info:', extractedCompanyInfo.extracted_info);

      // 検索クエリを生成 (propsで渡されていない場合は自動生成)
      const finalSearchQuery = searchQuery || generateSearchQuery(extractedCompanyInfo);

      const request: RestorePolicyGenerationRequest = {
        company_name: extractedCompanyInfo.company_name!,
        business_type: extractedCompanyInfo.business_type!,
        location: extractedCompanyInfo.location!,
        main_business: extractedCompanyInfo.main_business,
        employee_count: extractedCompanyInfo.employee_count,
        annual_revenue: extractedCompanyInfo.annual_revenue,
        suppliers: extractedCompanyInfo.suppliers,
        customers: extractedCompanyInfo.customers,
        include_hazard_analysis: true,
        use_parallel_generation: true,
        use_section_based_generation: true,
        extracted_info: extractedCompanyInfo.extracted_info,
        search_query: finalSearchQuery,
        max_search_results: maxSearchResults,
        tenant_id: tenantId,
      };

      // 非同期ジョブ作成
      const response = await generateRestorePolicyAsync(request);
      console.log('=== generateRestorePolicyAsync Response in Hook ===');
      console.log('Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response || {}));
      console.log('job_id:', response.job_id);
      console.log('=== END Hook Debug ===');

      const job_id = response.job_id;
      if (!job_id) {
        throw new Error('job_id is undefined in response');
      }

      setJobStatus('pending');

      // ポーリング開始
      await pollJobStatus(job_id);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || '復旧方針の生成中にエラーが発生しました');
    }
  };

  // 再生成
  const regenerate = async () => {
    hasExecutedRef.current = false;
    setHasGenerated(false);
    await generate();
  };

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      cleanupPolling();
    };
  }, []);

  // 自動生成(初回のみ)
  useEffect(() => {
    // skipAutoGenerationがtrueの場合は何もしない
    if (skipAutoGeneration) {
      return;
    }

    // 既に実行済みの場合は何もしない
    if (hasExecutedRef.current) {
      return;
    }

    // ファイル+apiResponse または companyInfo がある場合に生成
    const hasData = (uploadedFiles.length > 0 && apiResponse) || companyInfo;
    if (!hasData) {
      return;
    }

    // 上記条件をすべてクリアした場合のみ生成
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles, apiResponse, companyInfo, skipAutoGeneration]);

  return {
    isLoading,
    formData,
    hasGenerated,
    error,
    jobStatus,
    elapsedSeconds,
    regenerate,
  };
};
